/**
 * Mapping Audit components for the Connectors page.
 *
 * Split out of `pages/Connectors.jsx` to keep that file under the 700-line
 * threshold flagged by the testing agent. These two components form a
 * self-contained surface: the inline summary chip (`MappingSummary`) lives
 * under each connector row; clicking its warnings badge opens the modal
 * (`MappingAuditModal`) which exposes the procurement-grade audit JSON.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Inline summary chip rendered under each connector row.
 *
 * Trust feature: visible explicit "we tell you when we inferred something"
 * statement, even on healthy syncs ("0 mapping warnings · servicenow:v1").
 * Procurement teams reward transparency more than perfection.
 */
export function MappingSummary({ c }) {
  const [auditOpen, setAuditOpen] = useState(false);
  const ms = c.last_mapping_summary;
  if (!ms || !ms.mapper_version) return null;
  const warnings = Number(ms.warnings_count || 0);
  return (
    <>
      <div className="mt-2 inline-flex flex-wrap items-center gap-2 text-[11px]"
           data-testid={`connector-mapping-summary-${c.connector_id}`}>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-slate-700">
          {ms.mapper_version}
        </span>
        {ms.fx_rate_source && ms.fx_rate_source !== "n/a" && (
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-700"
                title="FX rate table used to normalise non-GBP financials. Override with ERIO_FX_TO_GBP_<ISO> env vars.">
            FX · {ms.fx_rate_source}
          </span>
        )}
        {warnings > 0 ? (
          <button onClick={() => setAuditOpen(true)}
                  data-testid={`connector-warnings-badge-${c.connector_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 font-semibold text-amber-800 hover:bg-amber-100"
                  title="Open the mapping audit — shows every record where ERIO made an inference + why.">
            <AlertTriangle size={10} /> {warnings} mapping warning{warnings === 1 ? "" : "s"}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-800"
                title="All synced records mapped cleanly into the canonical model — no inferences made.">
            <CheckCircle2 size={10} /> 0 mapping warnings
          </span>
        )}
      </div>
      {auditOpen && (
        <MappingAuditModal connectorId={c.connector_id} connectorName={c.name}
                           onClose={() => setAuditOpen(false)} />
      )}
    </>
  );
}

/**
 * Audit modal — flagged records list with downloadable JSON for procurement.
 */
function MappingAuditModal({ connectorId, connectorName, onClose }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/connectors/${connectorId}/mapping-audit?limit=50`)
      .then((r) => setAudit(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load audit"))
      .finally(() => setLoading(false));
  }, [connectorId]);

  const download = () => {
    if (!audit) return;
    const blob = new Blob([JSON.stringify(audit, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `erio-mapping-audit-${connectorId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-6 overflow-y-auto"
         data-testid="mapping-audit-modal" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="ek-eyebrow">Canonical mapping audit · {connectorName}</p>
            <h3 className="font-display text-lg font-bold text-ek-ink">Inferences ERIO made on the last sync</h3>
            <p className="mt-1 text-xs text-ek-ink/60">
              Every record where the mapper had to default, infer, or normalise. Procurement-grade audit trail —
              we surface every assumption rather than silently apply it.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close mapping audit"
                  className="rounded-md px-2 py-1 text-sm text-ek-ink/60 hover:bg-slate-100">✕</button>
        </div>
        {loading && <p className="mt-4 text-sm text-ek-ink/60">Loading audit…</p>}
        {audit && (
          <>
            <div className="mt-4 grid grid-cols-3 gap-3 text-xs"
                 data-testid="mapping-audit-summary">
              <SummaryStat label="Records synced" value={audit.summary.records_synced} />
              <SummaryStat label="With warnings" value={audit.summary.records_with_warnings} amber />
              <SummaryStat label="Mapper / FX" value={`${audit.summary.mapper_version || "—"} · ${audit.summary.fx_rate_source || "—"}`} small />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">
                Flagged records ({audit.flagged_records.length})
              </p>
              <button onClick={download} data-testid="mapping-audit-download"
                      className="rounded-md border border-black/10 bg-white px-2.5 py-1 text-xs font-medium text-ek-ink hover:bg-ek-purple/5">
                Download JSON
              </button>
            </div>
            {audit.flagged_records.length === 0 ? (
              <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                No records carry mapping warnings on this sync — the canonical model accepted every row cleanly.
              </p>
            ) : (
              <ul className="mt-3 max-h-[55vh] space-y-2 overflow-y-auto pr-1"
                  data-testid="mapping-audit-records">
                {audit.flagged_records.map((rec) => (
                  <li key={rec.external_id} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-ek-ink">
                        <span className="font-mono text-ek-ink/70">{rec.external_id}</span> · {rec.title}
                      </p>
                      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
                        {rec.status}
                      </span>
                    </div>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-[11px] text-amber-900">
                      {(rec.mapping_warnings || []).map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const SummaryStat = ({ label, value, amber, small }) => (
  <div className={`rounded-lg border p-2 text-center ${amber ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
    <p className="text-[10px] uppercase tracking-wide text-ek-ink/65">{label}</p>
    <p className={`mt-1 font-display ${small ? "text-xs" : "text-base"} font-bold text-ek-ink`}>{value}</p>
  </div>
);
